import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Plus, X, Bell, BellOff, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { isValidEmail } from '../utils/claimHelpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENTS = [
  { key: 'status_change',      label: 'Status changed' },
  { key: 'claim_closed',       label: 'Claim closed' },
  { key: 'settlement_entered', label: 'Settlement amount entered' },
  { key: 'claim_created',      label: 'New claim created' },
  { key: 'note_added',         label: 'Note added' },
  { key: 'documents_uploaded', label: 'Documents/media uploaded' },
];

/**
 * Inline notification recipients + per-event toggle card.
 * Renders inside the Company edit dialog (only when editing an existing company).
 */
export default function NotificationRecipientsCard({ companyName }) {
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);
  const [recipients, setRecipients] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [portalEnabled, setPortalEnabled] = useState(true);
  const [notifyOn, setNotifyOn] = useState({});
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [savingRecipients, setSavingRecipients] = useState(false);

  const fetchCompany = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/companies/${encodeURIComponent(companyName)}`, { withCredentials: true });
      const c = res.data || {};
      setRecipients(Array.isArray(c.notification_recipients) ? c.notification_recipients : []);
      setEnabled(c.notifications_enabled !== false);
      setPortalEnabled(c.portal_enabled !== false);
      setNotifyOn(c.notify_on || {
        status_change: true, claim_closed: true, settlement_entered: true,
        claim_created: false, note_added: false, documents_uploaded: false,
      });
    } catch (err) {
      if (err.response?.status !== 404) {
        toast.error('Failed to load notification settings');
      }
    } finally {
      setLoading(false);
    }
  }, [companyName]);

  useEffect(() => { fetchCompany(); }, [fetchCompany]);

  const persistRecipients = async (list) => {
    setSavingRecipients(true);
    try {
      const res = await axios.put(
        `${API}/companies/${encodeURIComponent(companyName)}/recipients`,
        { recipients: list },
        { withCredentials: true }
      );
      setRecipients(res.data.recipients);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save recipients');
    } finally {
      setSavingRecipients(false);
    }
  };

  const addRecipient = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !isValidEmail(email)) {
      toast.error('Enter a valid email (e.g. alex.chase@bhspecialty.com)');
      return;
    }
    if (recipients.some((r) => r.email.toLowerCase() === email)) {
      toast.warning('Already added');
      return;
    }
    const next = [
      ...recipients,
      { email, name: newName.trim(), primary: recipients.length === 0 },
    ];
    await persistRecipients(next);
    setNewEmail(''); setNewName('');
  };

  const removeRecipient = async (email) => {
    await persistRecipients(recipients.filter((r) => r.email !== email));
  };

  const setPrimary = async (email) => {
    await persistRecipients(recipients.map((r) => ({ ...r, primary: r.email === email })));
  };

  const updateSettings = async (patch) => {
    try {
      const res = await axios.put(
        `${API}/companies/${encodeURIComponent(companyName)}/notify-settings`,
        patch,
        { withCredentials: true }
      );
      if (patch.notifications_enabled !== undefined) setEnabled(res.data.notifications_enabled);
      if (patch.portal_enabled !== undefined) setPortalEnabled(res.data.portal_enabled);
      if (patch.notify_on) setNotifyOn(res.data.notify_on || notifyOn);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update settings');
    }
  };

  const toggleEvent = (key) => {
    const next = { ...notifyOn, [key]: !notifyOn[key] };
    setNotifyOn(next);
    updateSettings({ notify_on: { [key]: next[key] } });
  };

  if (loading) {
    return (
      <div className="border border-gray-200 rounded-lg px-4 py-3 mt-4 flex items-center justify-center text-gray-400">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading notifications…
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg mt-4 overflow-hidden" data-testid="recipients-card">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full px-4 py-3 flex items-center gap-2 hover:bg-gray-50 transition-colors"
        data-testid="recipients-card-toggle"
      >
        {open ? <ChevronDown className="w-4 h-4 text-teal-500" /> : <ChevronRight className="w-4 h-4 text-teal-500" />}
        {enabled
          ? <Bell className="w-4 h-4 text-teal-500" />
          : <BellOff className="w-4 h-4 text-gray-400" />}
        <span className="text-sm font-medium text-gray-800">Customer notifications</span>
        <span className="ml-auto text-xs text-gray-500">
          {enabled ? `${recipients.length} recipient${recipients.length === 1 ? '' : 's'}` : 'disabled'}
        </span>
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 space-y-4 border-t border-gray-100 bg-gray-50/40">
          {/* Master switch */}
          <label className="flex items-center gap-2 text-xs">
            <Checkbox
              checked={enabled}
              onCheckedChange={(v) => updateSettings({ notifications_enabled: !!v })}
              data-testid="recipients-master-toggle"
            />
            <span className="text-gray-700">Enable customer email notifications for this company</span>
          </label>

          {/* Magic-link portal toggle */}
          <label className="flex items-start gap-2 text-xs">
            <Checkbox
              checked={portalEnabled}
              disabled={!enabled}
              onCheckedChange={(v) => updateSettings({ portal_enabled: !!v })}
              data-testid="recipients-portal-toggle"
            />
            <span className={enabled ? 'text-gray-700' : 'text-gray-400'}>
              Include a secure "View Claim Online" link in each email
              <span className="block text-[11px] text-gray-400 mt-0.5">
                Customers see a read-only summary — no internal notes or amounts. Link expires 30 days after each send.
              </span>
            </span>
          </label>

          {/* Recipients */}
          <div>
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Email recipients</p>
            {recipients.length === 0 && (
              <p className="text-xs text-gray-400 italic mb-2">No recipients yet.</p>
            )}
            <div className="space-y-1.5">
              {recipients.map((r) => (
                <div key={r.email} className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-1.5" data-testid={`recipient-${r.email}`}>
                  <Mail className="w-3.5 h-3.5 text-gray-400" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-gray-800 truncate">{r.email}</div>
                    {r.name && <div className="text-xs text-gray-500 truncate">{r.name}</div>}
                  </div>
                  {r.primary ? (
                    <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-teal-100 text-teal-700 font-semibold">Primary</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setPrimary(r.email)}
                      className="text-[10px] text-gray-400 hover:text-teal-600"
                      data-testid={`recipient-set-primary-${r.email}`}
                    >
                      Make primary
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => removeRecipient(r.email)}
                    className="text-gray-400 hover:text-red-500"
                    data-testid={`recipient-remove-${r.email}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-end gap-2 mt-2">
              <div className="flex-1 min-w-[180px]">
                <Input
                  type="email"
                  placeholder="email@customer.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="h-8 text-sm"
                  data-testid="recipient-new-email"
                />
              </div>
              <div className="w-40">
                <Input
                  placeholder="Name (optional)"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="h-8 text-sm"
                  data-testid="recipient-new-name"
                />
              </div>
              <Button
                type="button"
                size="sm"
                onClick={addRecipient}
                disabled={savingRecipients}
                className="h-8 bg-teal-500 hover:bg-teal-600"
                data-testid="recipient-add-btn"
              >
                {savingRecipients ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
                Add
              </Button>
            </div>
          </div>

          {/* Per-event toggles */}
          <div>
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Auto-notify recipients on</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {EVENTS.map((e) => (
                <label key={e.key} className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded ${notifyOn[e.key] ? 'bg-teal-50' : 'bg-white'} border border-gray-200`}>
                  <Checkbox
                    checked={!!notifyOn[e.key]}
                    onCheckedChange={() => toggleEvent(e.key)}
                    disabled={!enabled}
                    data-testid={`notify-event-${e.key}`}
                  />
                  <span className={notifyOn[e.key] ? 'text-teal-800' : 'text-gray-600'}>{e.label}</span>
                </label>
              ))}
            </div>
            <p className="text-[10px] text-gray-400 mt-2">
              Defaults: status changes, claim closures, and settlement entries notify customers. Toggle to add or remove events.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
